import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { ArrowLeft, LogOut, Loader2, RefreshCw, Inbox, CalendarDays, CheckCircle2, Undo2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-lg border border-[#E5E2DC] bg-white px-4 py-3 text-sm text-[#1A1C19] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#244A32]/30 focus:border-[#244A32] transition";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function formatDate(iso) {
  try {
    return format(parseISO(iso), "d. MMMM yyyy", { locale: de });
  } catch {
    return iso || "–";
  }
}

export default function Admin() {
  const [user, setUser] = useState(null); // null=checking, false=logged out, object=logged in
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [bookings, setBookings] = useState(null);

  const loadBookings = useCallback(async () => {
    setBookings(null);
    try {
      const { data } = await axios.get(`${API}/bookings`, { withCredentials: true });
      setBookings(data);
    } catch {
      setBookings([]);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        setUser(data);
        loadBookings();
      })
      .catch(() => setUser(false));
  }, [loadBookings]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      loadBookings();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setBookings(null);
  };

  const toggleDone = async (id) => {
    try {
      const { data } = await axios.patch(`${API}/bookings/${id}/done`, {}, { withCredentials: true });
      setBookings((list) => list.map((b) => (b.id === id ? { ...b, done: data.done } : b)));
    } catch {
      /* ignore */
    }
  };

  const deleteBooking = async (id, name) => {
    if (!window.confirm(`Anfrage von "${name}" wirklich endgültig löschen?`)) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, { withCredentials: true });
      setBookings((list) => list.filter((b) => b.id !== id));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1A1C19]" data-testid="admin-page">
      <header className="border-b border-[#E5E2DC] bg-[#F9F8F6]/80 backdrop-blur-xl">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="admin-brand-link">
            <img src="/logo.jpg" alt="LLP Zaun&Garten Logo" className="h-10 w-auto rounded-md" />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-back-link"
              className="flex items-center gap-2 text-sm font-semibold text-[#244A32] hover:text-[#1D3B28] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Zur Startseite</span>
            </Link>
            {user && (
              <button
                onClick={doLogout}
                data-testid="admin-logout-button"
                className="flex items-center gap-2 text-sm font-semibold text-[#575953] hover:text-[#244A32] border border-[#E5E2DC] rounded-full px-4 py-2 transition-colors"
              >
                <LogOut className="w-4 h-4" /> Abmelden
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-8 lg:px-16 max-w-5xl mx-auto py-12 sm:py-16">
        {user === null && (
          <div className="flex justify-center py-24" data-testid="admin-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#244A32]" />
          </div>
        )}

        {user === false && (
          <div className="max-w-md mx-auto bg-white rounded-2xl border border-[#E5E2DC] p-8 sm:p-10 mt-8" data-testid="admin-login-card">
            <h1 className="font-heading text-2xl font-bold tracking-tight">Admin-Bereich</h1>
            <p className="text-sm text-[#575953] mt-2">Melden Sie sich an, um eingegangene Anfragen zu sehen.</p>
            <form onSubmit={submitLogin} className="mt-8 space-y-5" data-testid="admin-login-form">
              <div>
                <label htmlFor="admin-email" className="block text-sm font-semibold mb-2">E-Mail</label>
                <input
                  id="admin-email"
                  data-testid="admin-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="block text-sm font-semibold mb-2">Passwort</label>
                <input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className={inputClass}
                />
              </div>
              {error && (
                <p className="text-sm font-medium text-red-600" data-testid="admin-login-error">{error}</p>
              )}
              <button
                type="submit"
                data-testid="admin-login-submit"
                disabled={busy}
                className="w-full bg-[#244A32] hover:bg-[#1D3B28] disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? "Anmeldung …" : "Anmelden"}
              </button>
            </form>
          </div>
        )}

        {user && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#244A32]">Admin</p>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-2" data-testid="admin-headline">
                  Eingegangene Anfragen
                </h1>
                <p className="text-sm text-[#575953] mt-2">Angemeldet als {user.name}</p>
              </div>
              <button
                onClick={loadBookings}
                data-testid="admin-refresh-button"
                className="flex items-center gap-2 text-sm font-semibold text-[#244A32] border border-[#E5E2DC] bg-white rounded-full px-4 py-2 hover:border-[#244A32] transition-colors self-start sm:self-auto"
              >
                <RefreshCw className="w-4 h-4" /> Aktualisieren
              </button>
            </div>

            {bookings === null && (
              <div className="flex justify-center py-20" data-testid="admin-bookings-loading">
                <Loader2 className="w-8 h-8 animate-spin text-[#244A32]" />
              </div>
            )}

            {bookings !== null && bookings.length === 0 && (
              <div className="mt-10 bg-white rounded-2xl border border-[#E5E2DC] p-14 text-center" data-testid="admin-bookings-empty">
                <Inbox className="w-10 h-10 text-zinc-300 mx-auto" />
                <p className="mt-4 text-sm text-[#575953]">Noch keine Anfragen eingegangen.</p>
              </div>
            )}

            {bookings !== null && bookings.length > 0 && (
              <div className="mt-8 space-y-4" data-testid="admin-bookings-list">
                {bookings.map((b) => (
                  <article
                    key={b.id}
                    className={`bg-white rounded-2xl border p-6 transition-opacity ${
                      b.done ? "border-[#244A32]/30 opacity-60" : "border-[#E5E2DC]"
                    }`}
                    data-testid={`booking-card-${b.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2.5">
                          <h2 className="font-heading font-semibold text-lg">{b.name}</h2>
                          <span className="text-xs font-bold uppercase tracking-widest bg-[#244A32]/10 text-[#244A32] px-3 py-1 rounded-full">
                            {b.service}
                          </span>
                          {b.done && (
                            <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-[#244A32] text-white px-3 py-1 rounded-full" data-testid={`booking-done-badge-${b.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> Erledigt
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-400 mt-1.5">Eingegangen am {formatDate(b.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {b.preferred_date && (
                          <span className="flex items-center gap-2 text-sm font-semibold text-[#1A1C19] bg-[#F1EFEA] px-4 py-2 rounded-full">
                            <CalendarDays className="w-4 h-4 text-[#244A32]" />
                            {formatDate(b.preferred_date)}
                          </span>
                        )}
                        <button
                          onClick={() => toggleDone(b.id)}
                          data-testid={`booking-done-toggle-${b.id}`}
                          className={`flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border transition-colors ${
                            b.done
                              ? "border-[#E5E2DC] text-[#575953] hover:text-[#244A32]"
                              : "border-[#244A32] text-[#244A32] hover:bg-[#244A32] hover:text-white"
                          }`}
                        >
                          {b.done ? (
                            <>
                              <Undo2 className="w-4 h-4" /> Wieder öffnen
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" /> Erledigt
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => deleteBooking(b.id, b.name)}
                          data-testid={`booking-delete-${b.id}`}
                          aria-label="Anfrage löschen"
                          title="Anfrage löschen"
                          className="w-10 h-10 rounded-full border border-[#E5E2DC] text-[#575953] hover:border-red-300 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-x-8 gap-y-1.5 text-sm text-[#575953]">
                      <a href={`mailto:${b.email}`} className="hover:text-[#244A32] font-medium" data-testid={`booking-email-${b.id}`}>{b.email}</a>
                      {b.phone && (
                        <a href={`tel:${b.phone.replace(/\s/g, "")}`} className="hover:text-[#244A32] font-medium" data-testid={`booking-phone-${b.id}`}>{b.phone}</a>
                      )}
                    </div>
                    {b.message && (
                      <p className="mt-4 text-sm text-[#575953] leading-relaxed bg-[#F9F8F6] rounded-xl p-4">{b.message}</p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
